import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { exchangeSessionId, fetchMe } from "../lib/api";
import { Loader2, LogIn, ShieldX } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function LoginPage() {
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-10 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-slate-900 text-white rounded-md flex items-center justify-center font-bold">VV</div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Viaja de Verdad</div>
            <div className="font-cabinet text-2xl font-bold tracking-tight text-slate-900">Facturación</div>
          </div>
        </div>
        <p className="text-sm text-slate-600 mb-8">
          Esta herramienta es privada. Solo cuentas autorizadas pueden acceder.
        </p>
        <Button
          onClick={handleLogin}
          className="w-full bg-slate-900 hover:bg-slate-800 h-11"
          data-testid="google-login-btn"
        >
          <LogIn size={16} className="mr-2" /> Iniciar sesión con Google
        </Button>
      </div>
    </div>
  );
}

export function AuthCallback() {
  const nav = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const m = window.location.hash.match(/session_id=([^&]+)/);
    const sessionId = m && decodeURIComponent(m[1]);
    if (!sessionId) {
      nav("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const user = await exchangeSessionId(sessionId);
        // Clear the URL hash
        window.history.replaceState(null, "", window.location.pathname);
        nav("/", { replace: true, state: { user } });
      } catch (err) {
        const msg = err?.response?.data?.detail || err.message || "Error iniciando sesión";
        setError(msg);
      }
    })();
  }, [nav]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white border border-rose-200 rounded-lg p-10 shadow-sm">
          <div className="flex items-center gap-3 mb-4 text-rose-700">
            <ShieldX size={22} />
            <div className="font-cabinet text-xl font-bold tracking-tight">Acceso denegado</div>
          </div>
          <p className="text-sm text-slate-700 mb-6">{error}</p>
          <Button onClick={() => nav("/login", { replace: true })} variant="outline" className="w-full">
            Volver al inicio de sesión
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center text-slate-500">
      <Loader2 size={20} className="animate-spin mr-2" /> Iniciando sesión...
    </div>
  );
}

export function ProtectedRoute({ children }) {
  const location = useLocation();
  const [state, setState] = useState(location.state?.user ? "ok" : "checking");
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (location.state?.user) return;
    (async () => {
      try {
        const u = await fetchMe();
        setUser(u);
        setState("ok");
      } catch {
        setState("denied");
      }
    })();
  }, [location.state]);

  if (state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400">
        <Loader2 size={20} className="animate-spin mr-2" /> Cargando...
      </div>
    );
  }
  if (state === "denied") return <Navigate to="/login" replace />;
  return typeof children === "function" ? children(user) : children;
}
